/**
 * Name: Edit Playlist Page
 * Author: @Gaurav
 * Date: 20-07-2021
 */
import React, { useState, useEffect, useRef } from 'react';
import './EditPlaylistAndServer.scss';
import { useLocation } from 'react-router-dom';
import EditPlaylist from './EditPlaylist';
import EditPlaylistServer from './EditPlaylistServer';
import { _showAlert } from '../../utils';
import { LoaderSpinner } from '../../components';
import { useDispatch, useSelector } from 'react-redux';
import { setSongsSelected } from '../../utils/songSelection';
import { fetcher } from '../../utils/axios';
import { buildPlaylistSavePayload } from '../../utils/safeEditPayloads';
import { prepareFreshPlaylistEditor } from '../../utils/playlistConflictRecovery';
import usePlaylistConflictRecovery from '../../utils/usePlaylistConflictRecovery';
import PlaylistConflictDialog from '../../components/PlaylistManagement/PlaylistConflictDialog';

function EditPlaylistAndServer(props) {
  const routeLocation = useLocation();
  const dispatch = useDispatch();

  // * state used when page is opened as popup
  const isPageOpenedAsPopup = props.isPageOpenedAsPopup;
  const selectedPlaylistForPopup = props.selectedPlaylistForPopup;
  const onSavePlaylistFromPopup = props.onSavePlaylist;
  const isPlaylistRoyaltyForPopup = props.isPlaylistRoyalty;

  const [selectedTab, setSelectedTab] = useState('playlistTabs');
  const [playlistToUpdate, setListToUpdate] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [selectedSongsToUpload, setSongsToUpload] = useState([]);
  const [selectedCurrentSongIds, setSelectedCurrentSongIds] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [selectedPlaylist, setSelectedPlaylist] = useState([]);
  const [currentPlayingSong, setCurrentPlayingSong] = useState(null);
  const [isSongPlaying, setIsSongPlaying] = useState(false);
  const [isSongLoading, setIsSongLoading] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [conflictOpen, setConflictOpen] = useState(false);

  const {
    updatePlaylist,
    getAllPlaylist: allPlaylist,
    songURL,
    songsList,
  } = useSelector((state) => ({
    updatePlaylist: state.updatePlaylist.result,
    getAllPlaylist: state.getAllPlaylist.result,
    songURL: state.getSongURL.result,
    songsList: state.searchSongs.result,
  }));

  const audioRef = useRef(null);

  const originalSongIds = useRef(null);
  const conflictRequested = useRef(false);

  const refreshConflict = async (draft) => {
    setConflict({ draft, latest: null, loading: true });
    setConflictOpen(true);
    try {
      const playlists = await fetcher('/playlists/');
      const latest = playlists.find((item) => item.id === playlistToUpdate.id);
      if (!latest) throw new Error('Playlist not found');
      setConflict({ draft, latest, loading: false });
    } catch (error) {
      setConflict({ draft, latest: null, loading: false });
    }
  };

  const reloadLatest = (recoveredPlaylist) => {
    const latest = recoveredPlaylist?.id ? recoveredPlaylist : conflict?.latest;
    if (!latest) return;
    setListToUpdate({ ...latest, originalName: latest.name, originalType: latest.type });
    setSongsToUpload(latest.songs);
    setSelectedCurrentSongIds([]);
    originalSongIds.current = latest.songs.map((song) => song.id);
    conflictRequested.current = false;
    setConflict(null);
    setConflictOpen(false);
  };

  const recovery = usePlaylistConflictRecovery({
    playlist: playlistToUpdate,
    originalSongIds: originalSongIds.current,
    draft: conflict ? selectedSongsToUpload : undefined,
    metadataChanged: playlistToUpdate.name !== playlistToUpdate.originalName || playlistToUpdate.type !== playlistToUpdate.originalType,
    onRecovered: (latest) => {
      reloadLatest(latest);
      _showAlert('Songs added. Latest playlist loaded; duplicates were skipped.', 'success');
      if (isPageOpenedAsPopup) onSavePlaylistFromPopup();
    },
  });
  const requestedPlaylist = isPageOpenedAsPopup ? selectedPlaylistForPopup : routeLocation?.state?.selectedPlaylist;
  const requestedPlaylistId = requestedPlaylist?.id;
  const requestedPlaylistRef = useRef(requestedPlaylist);
  requestedPlaylistRef.current = requestedPlaylist;

  const handlePlaylistTabClick = (tabName = {}) => {
    setSelectedTab(tabName);
    setSearchInput('');
  };

  const urlData = props.location.pathname.split('/');
  const isRoyalties = urlData.includes('royalties') || isPlaylistRoyaltyForPopup;

  useEffect(() => {
    if (selectedTab === 'playlistTabs') {
      dispatch({ type: 'GET_ALL_PLAYLIST', payload: searchInput });
    } else {
      dispatch({ type: 'SEARCH_SONGS', payload: { search: searchInput } });
    }
  }, [searchInput, selectedTab]);

  useEffect(() => {
    const requested = requestedPlaylistRef.current;
    if (!requested?.id) return;
    let active = true;
    const initialize = (playlist) => {
      if (!active) return;
      const prepared = prepareFreshPlaylistEditor(requested, playlist);
      setListToUpdate(prepared);
      setSongsToUpload(prepared.songs);
      originalSongIds.current = prepared.originalSongIds;
      setSelectedCurrentSongIds([]);
    };

    setLoading(true);
    fetcher('/playlists/')
      .then((playlists) => {
        const playlist = playlists.find((item) => item.id === requested.id);
        if (!playlist || !Array.isArray(playlist.songs)) throw new Error('Playlist not found');
        initialize(playlist);
      })
      .catch(() => { if (active) _showAlert('Could not load the latest playlist songs. Close and reopen the editor to try again.', 'error'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [requestedPlaylistId]);

  useEffect(() => {
    if (updatePlaylist.id) {
      setLoading(false);
      _showAlert('Playlist Successfully Updated', 'success');
      // props.history.push(
      //   // '/edit-playlist-main'
      //   isRoyalties ? '/royalties/edit-playlist-main' : '/non-royalties/edit-playlist-main'
      // );

      dispatch({ type: 'CLEAR_STATE', actionName: 'UPDATE_PLAYLIST' });

      if (isPageOpenedAsPopup) {
        onSavePlaylistFromPopup();
        return;
      }

      props.history.push({
        pathname: isRoyalties ? '/royalties/edit-playlist-secondery' : '/non-royalties/edit-playlist-secondery',
        state: { selectedPlayList: routeLocation?.state?.selectedPlaylist },
      });
    }
    if (updatePlaylist.error) {
      setLoading(false);
      if (updatePlaylist.code === 409) {
        if (!conflictRequested.current) {
          conflictRequested.current = true;
          refreshConflict(selectedSongsToUpload);
        }
        dispatch({ type: 'CLEAR_STATE', actionName: 'UPDATE_PLAYLIST' });
      } else {
        _showAlert(updatePlaylist.error, 'error');
      }
    }
  }, [updatePlaylist, routeLocation]);

  useEffect(() => {
    return () => {
      dispatch({ type: 'CLEAR_STATE', actionName: 'UPDATE_PLAYLIST' });
    };
  }, []);

  useEffect(() => {
    if (songURL.status === 200 && songURL.url) {
      window.open(songURL.url, '_blank');
      dispatch({ type: 'CLEAR_STATE', actionName: 'GET_SONG_URL' });
    }
    if (songURL?.error) {
      _showAlert(songURL?.error, 'error');
    }
  }, [songURL]);

  const handleUpdatePlaylist = () => {
    if (conflict) {
      setConflictOpen(true);
      return;
    }
    if (!playlistToUpdate.id || playlistToUpdate.id !== requestedPlaylistId || !Array.isArray(originalSongIds.current) || isLoading) {
      _showAlert('Playlist is still loading. Please try again.', 'error');
      return;
    }
    setLoading(true);
    dispatch({ type: 'UPDATE_PLAYLIST', payload: buildPlaylistSavePayload({
      id: playlistToUpdate.id,
      originalSongIds: originalSongIds.current,
      songs: selectedSongsToUpload,
      originalName: playlistToUpdate.originalName,
      name: playlistToUpdate.name,
      originalType: playlistToUpdate.originalType,
      type: playlistToUpdate.type,
    }) });
  };

  const handleMultipleSongsToUpload = (event, songlist) => {
    const checked = event.target.checked;
    setSongsToUpload((current) => setSongsSelected(current, songlist || [], checked));
  };

  const handleSelectSongsToUpload = (event, song) => {
    const checked = event.target.checked;
    setSongsToUpload((current) => setSongsSelected(current, [song], checked));
  };

  const handleRemoveSong = (song) => {
    setSongsToUpload((current) => current.filter((item) => item.id !== song.id));
    setSelectedCurrentSongIds((current) => current.filter((id) => id !== song.id));
  };

  const handleSelectCurrentSongs = (songs, checked) => {
    const ids = songs.map((song) => song.id);
    setSelectedCurrentSongIds((current) => checked
      ? [...new Set([...current, ...ids])]
      : current.filter((id) => !ids.includes(id)));
  };

  const handleRemoveSelectedSongs = () => {
    const ids = new Set(selectedCurrentSongIds);
    setSongsToUpload((current) => current.filter((song) => !ids.has(song.id)));
    setSelectedCurrentSongIds([]);
  };

  const handleSelectPlaylist = (event, playList) => {
    const checked = event.target.checked;
    setSelectedPlaylist((current) => setSongsSelected(current, [playList], checked));
  };

  const handlePlaySong = async (song) => {
    // dispatch({ type: 'GET_SONG_URL', payload: `${song.id}.mp3` });
    console.log('song', audioRef);
    if (!audioRef.current) {
      // show error;
      console.log('error');
      return;
    }
    const prevSong = currentPlayingSong;
    setCurrentPlayingSong(song);

    if (prevSong?.id === song.id) {
      if (isSongPlaying) {
        audioRef.current.pause();
        setIsSongPlaying(false);
        return;
      }
      setIsSongLoading(true);
      audioRef.current.src = `${process.env.REACT_APP_API_URL}/songs/${song.id}.mp3`;
      audioRef.current.play().then(() => {
        console.log('playing');
        setIsSongLoading(false);
        setIsSongPlaying(true);
      });
      return;
    }

    if (isSongPlaying) {
      audioRef.current.pause();
      setIsSongPlaying(false);
    }

    setIsSongLoading(true);
    audioRef.current.src = `${process.env.REACT_APP_API_URL}/songs/${song.id}.mp3`;
    audioRef.current.play().then(() => {
      setIsSongLoading(false);
      setIsSongPlaying(true);
    });
  };

  return (
    <>
      <audio ref={audioRef} crossOrigin="anonymous" />
      {isLoading && <LoaderSpinner />}
      {conflict && !conflictOpen && <div role="alert" className="playlist-management-error">
        This playlist changed elsewhere. <button type="button" onClick={() => setConflictOpen(true)}>Review both song lists</button>
      </div>}
      <PlaylistConflictDialog
        conflict={conflict ? { ...conflict, draft: selectedSongsToUpload } : null}
        open={conflictOpen}
        onClose={() => setConflictOpen(false)}
        onReload={reloadLatest}
        onAddAndReload={recovery.canAdd ? recovery.addAndReload : undefined}
        reloadOnly={recovery.reloadOnly}
        busy={recovery.busy}
        recoveryError={recovery.error}
        onRetry={() => refreshConflict(selectedSongsToUpload)}
      />
      {selectedTab === 'playlistTabs' ? (
        <EditPlaylist
          isRoyalties={isRoyalties}
          onUpdatePlaylist={handleUpdatePlaylist}
          onPlaylistTabClick={handlePlaylistTabClick}
          playlistToUpdate={playlistToUpdate}
          selectedSongsToUpload={selectedSongsToUpload}
          selectedCurrentSongIds={selectedCurrentSongIds}
          handleSelectCurrentSongs={handleSelectCurrentSongs}
          handleRemoveSelectedSongs={handleRemoveSelectedSongs}
          handleSelectSongsToUpload={handleSelectSongsToUpload}
          handleMultipleSongsToUpload={handleMultipleSongsToUpload}
          handleRemoveSong={handleRemoveSong}
          handleSearchInput={setSearchInput}
          searchInput={searchInput}
          allPlaylist={allPlaylist}
          selectedPlaylist={selectedPlaylist}
          handleSelectPlaylist={handleSelectPlaylist}
          handlePlaySong={handlePlaySong}
          isSongPlaying={isSongPlaying}
          isSongLoading={isSongLoading}
          currentPlayingSong={currentPlayingSong}
          isPageOpenedAsPopup={isPageOpenedAsPopup}
          {...props}
        />
      ) : (
        <EditPlaylistServer
          onUpdatePlaylist={handleUpdatePlaylist}
          onPlaylistTabClick={handlePlaylistTabClick}
          selectedSongsToUpload={selectedSongsToUpload}
          selectedCurrentSongIds={selectedCurrentSongIds}
          handleSelectCurrentSongs={handleSelectCurrentSongs}
          handleRemoveSelectedSongs={handleRemoveSelectedSongs}
          handleSelectSongsToUpload={handleSelectSongsToUpload}
          handleMultipleSongsToUpload={handleMultipleSongsToUpload}
          handleRemoveSong={handleRemoveSong}
          handleSearchInput={setSearchInput}
          searchInput={searchInput}
          handlePlaySong={handlePlaySong}
          songsList={songsList}
          isPageOpenedAsPopup={isPageOpenedAsPopup}
          {...props}
        />
      )}
    </>
  );
}

export default EditPlaylistAndServer;
